import MonacoEditor from '@monaco-editor/react'
import { useCallback, useEffect, useState } from 'react'

import { DEFAULT_INDICATOR_CODE, useCustomIndicatorStore } from '../../stores/customIndicatorStore'
import type { CustomIndicator } from '../../stores/customIndicatorStore'
import { useThemeStore } from '../../stores/themeStore'

interface Props {
  indicator: CustomIndicator | null
  onClose: () => void
}

const COLORS = [
  '#2962ff',
  '#26a69a',
  '#ef5350',
  '#ffeb3b',
  '#ff9800',
  '#ab47bc',
  '#ffffff',
  '#e91e63',
]

export function CustomIndicatorEditor({ indicator, onClose }: Props) {
  const { addIndicator, updateIndicator } = useCustomIndicatorStore()
  const { theme } = useThemeStore()
  const isDark = theme === 'dark'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [code, setCode] = useState(DEFAULT_INDICATOR_CODE)
  const [color, setColor] = useState('#2962ff')
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    if (indicator) {
      setName(indicator.name)
      setDescription(indicator.description)
      setCode(indicator.code)
      setColor(indicator.color)
      setErrors([])
    } else {
      setName('')
      setDescription('')
      setCode(DEFAULT_INDICATOR_CODE)
      setColor('#2962ff')
      setErrors([])
    }
  }, [indicator])

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      setErrors(['Indicator name is required'])
      return
    }
    if (indicator) {
      updateIndicator(indicator.id, { name: name.trim(), description, code, color })
    } else {
      addIndicator({ name: name.trim(), description, code, color, enabled: true })
    }
    onClose()
  }, [indicator, name, description, code, color, addIndicator, updateIndicator, onClose])

  return (
    <div className="flex h-full flex-col bg-tv-bg-primary">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-tv-border px-4 py-3">
        <h2 className="text-sm font-semibold text-tv-text-primary">
          {indicator ? 'Edit Indicator' : 'New Indicator'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-tv-text-muted hover:text-tv-text-primary"
        >
          ✕
        </button>
      </div>

      {/* Fields */}
      <div className="flex flex-col gap-3 border-b border-tv-border px-4 py-3">
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="mb-1 block text-[10px] text-tv-text-muted">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Indicator"
              className="w-full rounded border border-tv-border bg-tv-bg-secondary px-2 py-1 text-xs text-tv-text-primary outline-none focus:border-tv-blue"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] text-tv-text-muted">Color</label>
            <div className="flex gap-1">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="h-5 w-5 rounded-full border-2 transition-transform hover:scale-110"
                  style={{ backgroundColor: c, borderColor: color === c ? 'white' : 'transparent' }}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[10px] text-tv-text-muted">
            Description (optional)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this indicator do?"
            className="w-full rounded border border-tv-border bg-tv-bg-secondary px-2 py-1 text-xs text-tv-text-primary outline-none focus:border-tv-blue"
          />
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="min-h-0 flex-1">
        <MonacoEditor
          language="javascript"
          theme={isDark ? 'vs-dark' : 'light'}
          value={code}
          onChange={(val) => setCode(val ?? '')}
          options={{
            fontSize: 12,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: 'on',
            tabSize: 2,
            wordWrap: 'on',
            automaticLayout: true,
            padding: { top: 8, bottom: 8 },
          }}
        />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="border-t border-tv-red/30 bg-tv-red/10 px-4 py-2">
          {errors.map((e, i) => (
            <p key={i} className="text-[10px] text-tv-red">
              {e}
            </p>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-tv-border px-4 py-3">
        <p className="text-[10px] text-tv-text-muted">
          Arrays:{' '}
          <code className="text-tv-text-secondary">open high low close volume timestamp</code>
          {' · '}
          Built-ins:{' '}
          <code className="text-tv-text-secondary">sma ema rsi atr macd highest lowest</code>
          {' · '}
          Output: <code className="text-tv-text-secondary">plot() plotHistogram()</code>
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1 text-xs text-tv-text-muted hover:text-tv-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded bg-tv-blue px-3 py-1 text-xs text-white hover:bg-tv-blue/90"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
